import React, { useMemo, useState } from 'react';
import { useIntl } from 'react-intl';
import clsx from 'clsx';
import { MUSIC_VIDEO_MIN_CLIP_SEC, type MusicVideoClip } from 'fym-shared/src/music24/musicVideoDoc';
import type { MusicVideoSongMapSection } from 'fym-shared/src/music24/musicVideoApi';
import { TypedMemo } from '../../common/types';
import { newDocId } from '../doc/clipIds';
import { removeClip, replaceClip, splitClipAt, updateClip } from '../doc/clipOps';
import { useClipLimits } from '../doc/useClipLimits';
import { pickSimilarFootage, usageCounts, type FootageView } from '../footage/footageView';
import { useSectionBankClips } from '../footage/useSectionBankClips';
import { useMusicVideoEditor } from '../MusicVideoEditorContext';
import { panelMessages } from '../messages/panelMessages';
import { useAiClip } from '../panels/footage/useAiClip';
import { splitPoint } from '../timeline/timelineGeometry';
/* eslint-disable css-modules/no-unused-class -- one stylesheet for the editor; each part uses its own slice of it */
import styles from '../MusicVideoEditor.module.scss';
/* eslint-enable css-modules/no-unused-class */

interface ClipActionsProps {
  clip: MusicVideoClip;
  view: FootageView | null;
  section: MusicVideoSongMapSection | null;
}

/**
 * The actions of a clip (design b1, c3, h3): replace it with a similar bank clip (the director's
 * picks for its section), keep it on recut, split it at a beat, remove it (the clip before fills
 * it); an AI clip also makes a variation (4 credits).
 */
const ClipActionsComponent: React.FC<ClipActionsProps> = ({ clip, view, section }) => {
  const intl = useIntl();
  const { doc, songMap, change, select, transport, readOnly, data } = useMusicVideoEditor();
  const limitsFor = useClipLimits();
  const ai = useAiClip();
  const [noSimilar, setNoSimilar] = useState(false);
  const suggest = useSectionBankClips(section);
  const similar = useMemo(
    () => pickSimilarFootage(suggest.views, clip.source, usageCounts(doc.clips)),
    [clip.source, doc.clips, suggest.views]
  );
  const split = splitPoint(clip, songMap.beats, transport.positionSec, MUSIC_VIDEO_MIN_CLIP_SEC);

  return (
    <div className={styles.column}>
      <button
        type="button"
        className={styles.ghostButton}
        disabled={readOnly || section === null}
        onClick={() => {
          if (similar === null) {
            setNoSimilar(true);
            return;
          }
          change((current) => replaceClip(current, clip.id, similar.source, limitsFor(similar)));
        }}
      >
        {intl.formatMessage(panelMessages.similar)}
      </button>
      {noSimilar && similar === null && <p className={styles.hint}>{intl.formatMessage(panelMessages.noSimilar)}</p>}
      <div className={styles.row}>
        <button
          type="button"
          className={clsx(styles.ghostButton, clip.keep && styles.ghostButtonOn)}
          aria-pressed={clip.keep}
          disabled={readOnly}
          onClick={() => change((current) => updateClip(current, clip.id, { keep: !clip.keep }))}
        >
          {intl.formatMessage(clip.keep ? panelMessages.kept : panelMessages.keep)}
        </button>
        <button
          type="button"
          className={styles.ghostButton}
          disabled={readOnly || split === null}
          onClick={() => {
            if (split !== null) {
              change((current) => splitClipAt(current, clip.id, split, newDocId()));
            }
          }}
        >
          {intl.formatMessage(panelMessages.split)}
        </button>
      </div>
      {view?.origin === 'ai' && view.prompt !== null && (
        <button
          type="button"
          className={styles.ghostButton}
          disabled={readOnly || ai.isBusy}
          onClick={() => {
            ai.make({ prompt: view.prompt ?? '', style: view.style ?? 'film', people: view.people ?? 'none' }).catch(
              console.error
            );
          }}
        >
          {intl.formatMessage(panelMessages.variation, { credits: data.plan.aiClipCredits })}
        </button>
      )}
      <button
        type="button"
        className={clsx(styles.ghostButton, styles.dangerButton)}
        disabled={readOnly || doc.clips.length < 2}
        onClick={() => {
          change((current) => removeClip(current, clip.id));
          select(null);
        }}
      >
        {intl.formatMessage(panelMessages.clipRemove)}
      </button>
    </div>
  );
};

export const ClipActions = TypedMemo(ClipActionsComponent);
